/**
 * Per-browser opt-out flag for first-party analytics. Used by team
 * members to keep their own interactions out of site analytics.
 *
 * Storage: both localStorage and a long-lived cookie (10y). Either one
 * being set counts as opted-out, so the flag survives clearing one
 * surface but not the other.
 */

import { supabase } from "@/integrations/supabase/client";
import { getVisitorId } from "@/lib/tracking";

const KEY = "dc_opt_out";
const TEN_YEARS_DAYS = 365 * 10;

/** Coarse browser bucket — enough to spot patterns, not enough to fingerprint. */
function uaFamily(): string {
  if (typeof navigator === "undefined") return "Unknown";
  const ua = navigator.userAgent || "";
  if (/Edg\//i.test(ua)) return "Edge";
  if (/OPR\/|Opera/i.test(ua)) return "Opera";
  if (/Firefox\//i.test(ua)) return "Firefox";
  if (/Chrome\//i.test(ua)) return "Chrome";
  if (/Safari\//i.test(ua)) return "Safari";
  return "Other";
}

/** Fire-and-forget: log an opt-out / undo action so staff can see the indicator. */
function logAction(action: "opt_out" | "undo"): void {
  try {
    let visitor_id: string | undefined;
    try {
      visitor_id = getVisitorId();
    } catch {
      /* ignore — visitor id is best-effort */
    }
    void supabase
      .rpc("log_analytics_opt_out" as never, {
        _action: action,
        _user_agent_family: uaFamily(),
        _visitor_id: visitor_id ?? null,
      } as never)
      .then(({ error }) => {
        if (error && import.meta.env.DEV) {
          console.warn("Analytics opt-out log failed", error);
        }
      });
  } catch {
    /* swallow — never block the UI on logging */
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const exp = new Date(Date.now() + days * 86400000).toUTCString();
  const secure =
    typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; expires=${exp}; path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

export function isOptedOut(): boolean {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem(KEY) === "1") {
      return true;
    }
  } catch {
    /* ignore */
  }
  return readCookie(KEY) === "1";
}

export function setOptOut(): void {
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    /* ignore */
  }
  writeCookie(KEY, "1", TEN_YEARS_DAYS);
  logAction("opt_out");
}

export function clearOptOut(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  deleteCookie(KEY);
  logAction("undo");
}
